// src/pages/profile-settings/patient/EditDob.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import supabase from "@/services/supabaseClient";

import DatePicker from "@/sot/primitives/datePicker.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { PATIENT_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";
import { formatLocalizedDayMonthYear } from "@/utils/localizedDateInput.js";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";

import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";

function isFrenchLanguageCode(languageCode) {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

function normalizeIsoDate(value = "") {
  const text = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) return text.slice(0, 10);
  return "";
}

export default function EditDob() {
  const nav = useNavigate();
  const { languageCode, t } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const [iso, setIso] = React.useState("");   // YYYY-MM-DD
  const [text, setText] = React.useState("");
  const [userId, setUserId] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [showDobPicker, setShowDobPicker] = React.useState(false);

  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
  });

  const canSave = Boolean(iso) && !saving;

  // ─────────────────────────────────────────────────────────────
  // Hydrate from DB first, then owner-scoped preview if DB is empty.
  // ─────────────────────────────────────────────────────────────
  React.useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      try {
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError || !user || cancelled) return;
        setUserId(user.id);

        const { data: profileRow, error: profError } = await supabase
          .from("patient_profiles")
          .select("date_of_birth")
          .eq("user_id", user.id)
          .maybeSingle();

        if (profError || cancelled) return;

        const dbIso = normalizeIsoDate(profileRow?.date_of_birth);
        if (dbIso) {
          const d = new Date(dbIso);
          if (!isNaN(d)) {
            setIso(dbIso);
            setText(
              formatLocalizedDayMonthYear(dbIso, languageCode)
            );
            return;
          }
        }

        const preview = loadPatientIntroPreview(user.id) || {};
        const previewIso = normalizeIsoDate(preview.dobIso);
        if (previewIso) {
          const d = new Date(previewIso);
          if (!isNaN(d) && !cancelled) {
            setIso(previewIso);
            setText(formatLocalizedDayMonthYear(previewIso, languageCode));
          }
        }
      } catch (e) {
        console.error("[Patient EditDob] hydrate error:", e);
      }
    }

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [languageCode]);

  function isoToDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return null;
    const [year, month, day] = String(value).split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function handleDateChange(nextDate) {
    if (!(nextDate instanceof Date) || Number.isNaN(nextDate.getTime())) {
      setIso("");
      setText("");
      return;
    }

    const year = nextDate.getFullYear();
    const month = String(nextDate.getMonth() + 1).padStart(2, "0");
    const day = String(nextDate.getDate()).padStart(2, "0");
    const nextIso = `${year}-${month}-${day}`;
    setIso(nextIso);
    setText(formatLocalizedDayMonthYear(nextIso, languageCode));
    setShowDobPicker(false);
  }

  async function handleSave() {
    if (!canSave) return;

    setSaving(true);

    try {
      // 1) Update local intro preview – keeps EditIntro in sync immediately
      const ownerId = userId || "";
      const current = ownerId ? loadPatientIntroPreview(ownerId) || {} : {};
      const nextPreview = { ...current, dobIso: iso };
      if (ownerId) savePatientIntroPreview(nextPreview, ownerId);

      // 2) Persist to Supabase when auth is available
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (!authError && user) {
        const { error } = await supabase
          .from("patient_profiles")
          .upsert(
            {
              user_id: user.id,
              date_of_birth: iso,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id" }
          );

        if (error) throw error;

        try {
          await supabase.auth.updateUser({
            data: {
              date_of_birth: iso,
              dob: iso,
            },
          });
        } catch {
          // metadata sync is non-blocking
        }
      }

      setSnackbar({
        open: true,
        message: t("settings.edit.dob.updated", isFrench ? "Date de naissance mise à jour" : "Date of birth updated"),
      });

      // Let the Snackbar show, then go back to EditIntro
      setTimeout(() => {
        setSnackbar((s) => ({ ...s, open: false }));
        nav(PATIENT_SETTINGS_PROFILE_ROUTE);
      }, 2200);
    } catch (e) {
      console.error("[Patient EditDob] save error:", e);
      setSnackbar({
        open: true,
        message: t(
          "settings.edit.common.saveFailed",
          isFrench ? "Impossible d’enregistrer les modifications. Veuillez réessayer." : "Could not save changes. Please try again."
        ),
      });
      setTimeout(() => {
        setSnackbar((s) => ({ ...s, open: false }));
      }, 2500);
    } finally {
      setSaving(false);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <SettingsScreenShell>
      <TopBar
        title={t("settings.edit.dob.title", isFrench ? "Modifier la date de naissance" : "Edit date of birth")}
        subtitle={t(
          "settings.edit.dob.subtitle",
          isFrench
            ? "Assurez-vous que cette date correspond à celle figurant sur votre pièce d’identité officielle."
            : "Make sure this matches your date of birth on your government issued ID."
        )}
        onBack={() => nav(PATIENT_SETTINGS_PROFILE_ROUTE)}
        trailing={
          <SaveChip disabled={!canSave} onClick={handleSave} />
        }
      />

      <div className="mt-5">
        <FolaOnboardingCard role="patient" />
      </div>

      <div className="mt-5 md:mt-6 space-y-3">
        <div
          className="cursor-pointer"
          onClick={() => setShowDobPicker((open) => !open)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setShowDobPicker((open) => !open);
            }
          }}
        >
          <TextField56
            id="patient-edit-dob-display"
            label={t("settings.edit.dob.label", isFrench ? "Date de naissance" : "Date of birth")}
            value={text || ""}
            onChange={() => {}}
            placeholder={t("settings.edit.dob.placeholder", isFrench ? "JJ MMM AAAA" : "DD MMM, YYYY")}
            readOnly
            tone="surface"
          />
        </div>
        {showDobPicker && (
          <div className="w-full max-w-[360px]">
            <DatePicker
              value={isoToDate(iso) || new Date()}
              onChange={handleDateChange}
              onCancel={() => setShowDobPicker(false)}
              mode="single"
            />
          </div>
        )}
      </div>

      {/* Global snackbar for success + errors (matches EditName) */}
      <Snackbar
        open={snackbar.open}
        onClose={() =>
          setSnackbar((s) => ({
            ...s,
            open: false,
          }))
        }
        duration={2200}
      >
        {snackbar.message}
      </Snackbar>
    </SettingsScreenShell>
  );
}
